import Vue from 'vue';
import BaseRepository from './BaseRepository';

class ListingRepository extends BaseRepository {

    /**
     * Makes initial post of space to database - only used once throughout listing process.
     *
     * @param {Object} data
     */
    post(data) {
        return Vue.http.post('/api/v2/listing', data);
    }

    /**
     * Patches space data to database
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    create(data) {
        return super.post(this.route('api.v2.listings.store'), data);
    }

    /**
     * Update a listing
     * Can include data such as features.
     *
     * @param {Number} space
     * @param {Object} data
     * @param {String} [changedStep]
     *
     * @returns {Promise}
     */
    update(space, data, changedStep = undefined) {
        return super.patch(
            this.route('api.v2.listings.update', {
                space,
                ...(changedStep ? { changed_step: changedStep } : {}),
            }),
            data
        ).then(this.normaliseResponse);
    }

    /**
     * Gets address from users inputted Postcode
     *
     * @param {String} postcode
     *
     * @returns {Promise}
     */
    getAddress(postcode) {
        return Vue.http.get(`listing/utility/postcode/${ postcode }/address`);
    }

    /**
     * Gets the validation rules and associated error messages for the additional_information field
     * @returns {Promise<Array>}
     */
    getAdditionalInformationRules() {
        return super.get(this.route('api.v2.listings.utilities.additional-information-rules'))
            .then(this.normaliseResponse);
    }

    /**
     * Gets all listings associated with the user that aren't archived
     */
    getUsersListings() {
        return Vue.http.get(this.route('api-v2.utility.listings'));
    }

    /**
     * Gets available space sizes
     *
     * @returns {Promise}
     */
    getSpaceSizes() {
        return super.get(this.route('api.v2.listings.utilities.sizes'))
            .then(this.normaliseResponse);
    }

    /**
     * Find a single listing
     *
     * @param {Number|String} space
     * @param {Object} data
     *
     * @returns {Promise}
     */
    find(space, data = {}) {
        return this.get(this.route('api.v2.listings.show', { space }), {
            include: ['options', data.includes].join(','),
            ...data,
        }).then((response) => {
            if (response.data.data.options) {
                const nearby = response.data.data.options.data.find(({ key }) => key === 'nearby_locations');

                response.data.data.options = {
                    nearby_locations: [],
                };

                if (nearby) {
                    response.data.data.options = {
                        nearby_locations: nearby.value || [],
                    };
                }
            }

            return response;
        }).then(this.normaliseResponse);
    }

    /**
     * Gets space listing with images from database
     *
     * @param spaceId
     *
     * @returns {Promise}
     */
    getListingImages(spaceId) {
        return Vue.http.get(`/api/v2/listing/${ spaceId }`, {
            params: {
                include: 'images',
            },
        });
    }

    /**
     * Gets recommended prices based on location for a space
     *
     * @param data
     *
     * @returns {Promise}
     */
    getPrices(data) {
        return Vue.http.get('recommended-prices', data);
    }

    /**
     * Get listing recurring availability
     *
     * @param spaceId
     *
     * @returns {Promise}
     */
    getRecurringAvailability(spaceId) {
        return Vue.http.get(`/api/v2/listing/${ spaceId }/availability/recurring`);
    }

    /**
     * Save each days recurring availability
     *
     * @param spaceId
     * @param day
     *
     * @returns {Promise}
     */
    saveRecurringDates(spaceId, day) {
        return Vue.http.patch(`/api/v2/listing/${ spaceId }/availability`, day);
    }

    /**
     * Saves specific granular availability settings
     *
     * @param spaceId
     * @param data
     *
     * @returns {Promise}
     */
    saveGranularAvailability(spaceId, data) {
        return Vue.http.patch(`/api/v2/listing/${ spaceId }/availability`, data);
    }

    /**
     * gets space availability for calendar
     *
     * @param spaceId
     * @param start
     *
     * @returns {Promise}
     */
    getAvailability(spaceId, start) {
        return Vue.http.get(`/api/v2/listing/${ spaceId }/availability/${ start }`);
    }

    /**
     * Deletes image from space listing
     *
     * @param spaceId
     * @param photoId
     *
     * @returns {Promise}
     */
    deleteImage(spaceId, photoId) {
        return Vue.http.delete(`/api/v2/listing/${ spaceId }/photo/${ photoId }`);
    }

    /**
     * sets default image on listing
     *
     * @param spaceId
     * @param photoId
     *
     * @returns {Promise}
     */
    setDefaultImage(spaceId, photoId) {
        return Vue.http.post(`/api/v2/listing/${ spaceId }/photo/${ photoId }/default`, {});
    }

    /**
     * reorders the photo on the listing
     *
     * @param spaceId
     * @param photos
     *
     * @returns {Promise}
     */
    reorderImages(spaceId, photos) {
        return Vue.http.post(`/api/v2/listing/${ spaceId }/photo/order`, photos);
    }

    /**
     * resets space's granular availability
     *
     * @param spaceId
     *
     * @returns {Promise}
     */
    resetCalendar(spaceId) {
        return Vue.http.post(`/api/v2/listing/${ spaceId }/reset-granular-availability`, {});
    }

    /**
     * Get available feature options
     *
     * @returns {Promise}
     */
    getFeatures() {
        return super.get(this.route('api.v2.listings.utilities.features'))
            .then(this.normaliseResponse);
    }

    /**
     * Get space details from backend
     *
     * @param {number|string} spaceId
     *
     * @returns {Promise}
     */
    getSpaceDetails(spaceId) {
        return super.get(this.route('api.v2.listings.space.details', { space: spaceId }));
    }

    /**
     * Delete a space
     */
    deleteSpace(space) {
        return Vue.http.delete(this.route('api.v2.listings.delete', { space: space.id }));
    }
}

export default new ListingRepository();
